import type { SellerPriceHistorySeries } from "./deal-offers-types";

export interface OfferHistoryComparisonPoint {
  time: number;
  selected: number | null;
  minimum: number | null;
  otherMin: number | null;
  otherMax: number | null;
  winnerId: string | null;
}

function availablePrice(
  intervals: SellerPriceHistorySeries["intervals"],
  time: number,
  terminal: boolean,
) {
  const interval = intervals.findLast(
    (interval) =>
      new Date(interval.fromAt).getTime() <= time &&
      (interval.toAt === null ||
        new Date(interval.toAt).getTime() > time ||
        (terminal && new Date(interval.toAt).getTime() === time)),
  );
  if (interval?.availability !== "available" || interval.amount === null) return null;
  const amount = Number(interval.amount);
  return Number.isFinite(amount) ? amount : null;
}

/** Evaluate explicit intervals at each boundary; unavailable and unobserved gaps stay empty. */
export function buildOfferHistoryComparison(
  series: SellerPriceHistorySeries[],
  offerId: string,
  checkedAt: Date | string | null,
  listingIntervals?: SellerPriceHistorySeries["intervals"],
): OfferHistoryComparisonPoint[] {
  const listing = listingIntervals ?? [];
  const boundaries = [...series.flatMap((seller) => seller.intervals), ...listing]
    .flatMap((interval) =>
      [interval.fromAt, interval.toAt]
        .filter((date): date is Date | string => date !== null)
        .map((date) => new Date(date).getTime()),
    )
    .filter(Number.isFinite);
  const checkedTime = checkedAt === null ? NaN : new Date(checkedAt).getTime();
  if (listingIntervals === undefined && Number.isFinite(checkedTime)) boundaries.push(checkedTime);
  if (!series.length && !listingIntervals?.length) return [];
  return [...new Set(boundaries)]
    .sort((a, b) => a - b)
    .map((time, index, times) => {
      const available = series
        .flatMap((seller) => {
          const price = availablePrice(seller.intervals, time, index === times.length - 1);
          return price === null ? [] : [{ id: seller.sellerOfferId, price }];
        })
        .sort((a, b) => a.price - b.price);
      const minimum =
        listingIntervals === undefined
          ? (available[0]?.price ?? null)
          : availablePrice(listingIntervals, time, index === times.length - 1);
      const others = available.filter((seller) => seller.id !== offerId);
      return {
        time,
        selected: selectedPrice(available, offerId),
        minimum,
        winnerId: available.find((seller) => seller.price === minimum)?.id ?? null,
        otherMin: others[0]?.price ?? null,
        otherMax: others.at(-1)?.price ?? null,
      };
    });
}

function selectedPrice(available: Array<{ id: string; price: number }>, offerId: string) {
  return available.find((seller) => seller.id === offerId)?.price ?? null;
}
